import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useSignalDefinitions, useSignals } from "../api/hooks";
import { authHeaders } from "../api/client";
import { Button } from "../components/ui";
import { Page, Table, Loading, ErrorBox, Empty, Chip } from "../components/ui";
import { SignalCard } from "../components/signal-card";
import { latestByScope, toneFor, unitOf } from "../lib/signals";
import { title } from "../lib/format";

export const Route = createFileRoute("/workforce")({ component: Workforce });

const scopes = [
  { v: "team", l: "Teams" },
  { v: "dept", l: "Departments" },
  { v: "manager", l: "Managers" },
  { v: "org", l: "Organisation" },
];

function Workforce() {
  const [scope, setScope] = useState("team");
  const [key, setKey] = useState<string | null>(null);
  const defs = useSignalDefinitions();
  const all = useSignals({ scope_type: scope });
  const latest = useMemo(() => (all.data ? latestByScope(all.data) : new Map()), [all.data]);
  const keys = useMemo(() => {
    const s = new Set<string>();
    for (const [k] of latest) s.add(k.split("|")[2]);
    return [...s].sort();
  }, [latest]);
  const wiKeys = ["meeting_hours_week", "meeting_load_pct", "external_meeting_pct", "after_hours_pct", "messages_per_day", "one_on_one_coverage_pct"];
  const selected = key ?? keys[0] ?? null;
  return (
    <Page
      title="Workforce"
      sub="Aggregated signals. Every number shows the population behind it; nothing renders below the privacy floor."
      actions={<Button variant="light" onClick={() => void exportCsv(scope)}>Export CSV →</Button>}
    >
      <div className="flex gap-2 mb-4">
        {scopes.map((s) => (
          <button key={s.v} className={`btn ${scope === s.v ? "btn-dark" : "btn-light"} !py-2 !px-3 !text-[12.5px]`} onClick={() => { setScope(s.v); setKey(null); }}>{s.l}</button>
        ))}
      </div>
      {all.isError && <ErrorBox error={all.error} />}
      {all.isPending ? (
        <Loading />
      ) : keys.length === 0 ? (
        <Empty title="Nothing above the floor yet" body="Signals appear after the nightly job runs on connected calendar and chat data, and only for groups of five or more." />
      ) : (
        <div className="grid gap-4" style={{ gridTemplateColumns: "260px 1fr" }}>
          <div className="card !p-2 self-start">
            {keys.map((k) => (
              <button key={k} className={`app-nav w-full text-left ${selected === k ? "active" : ""}`} onClick={() => setKey(k)}>{k}</button>
            ))}
          </div>
          <div>
            <div className="grid grid-cols-3 gap-3">
              {wiKeys.map((k) => (
                <SignalCard key={k} label={title(k).replace(" Pct", " %")} unit={unitOf(defs.data, k)} entry={selected ? latest.get(`${k}|${scope}|${selected}`) : undefined} />
              ))}
            </div>
            <h3 className="text-[15px] font-bold mt-6 mb-2">History</h3>
            <Table
              rows={selected ? [...latest.entries()].filter(([k]) => k.endsWith(`|${scope}|${selected}`)).flatMap(([, v]) => v.series).sort((a, b) => (a.period_start < b.period_start ? 1 : -1)) : []}
              keyOf={(s) => `${s.signal_key}${s.period_start}`}
              cols={[
                { h: "Week", cell: (s) => s.period_start, w: "120px" },
                { h: "Signal", cell: (s) => title(s.signal_key) },
                { h: "Value", cell: (s) => <span className="font-semibold">{s.value.toLocaleString(undefined, { maximumFractionDigits: 1 })}{unitOf(defs.data, s.signal_key) === "pct" ? "%" : ""}</span>, w: "110px" },
                { h: "Level", cell: (s) => { const t = toneFor(s.signal_key, s.value); return t === "neutral" ? <span className="text-muted">—</span> : <Chip tone={t}>{title(t)}</Chip>; }, w: "110px" },
                { h: "Population", cell: (s) => s.population_n, w: "100px" },
                { h: "Confidence", cell: (s) => <span className="text-muted">{s.confidence ?? "—"}</span>, w: "100px" },
              ]}
              empty="No history for this scope"
            />
          </div>
        </div>
      )}
    </Page>
  );
}

/** The same floor-applied rows the page shows, as a file. */
async function exportCsv(scope: string) {
  const res = await fetch(`/api/v1/signals/export.csv?scope_type=${encodeURIComponent(scope)}`, { headers: await authHeaders() });
  if (!res.ok) return;
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = res.headers.get("content-disposition")?.match(/filename="([^"]+)"/)?.[1] ?? "humindly-signals.csv";
  a.click();
  URL.revokeObjectURL(url);
}
